import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { BaseApiService } from './base-api.service';
import {
    AdditionalRuleModel,
    AdditionalRuleModelPagedResponseModel,
    DrainageVentilationRuleModel,
    DrainageVentilationRuleModelPagedResponseModel,
    GlazingRuleModel,
    GlazingRuleModelPagedResponseModel,
    IntegratedRuleModel,
    IntegratedRuleModelPagedResponseModel,
    JointRuleModel,
    JointRuleModelPagedResponseModel,
    LimitationRuleModel,
    LimitationRuleModelPagedResponseModel,
    MatingRuleModel,
    MatingRuleModelPagedResponseModel,
    QueryParams
} from '../models';

/**
 * Rules API Service - Handles all rule-related operations
 */
@Injectable({
    providedIn: 'root'
})
export class RulesService extends BaseApiService {

    // Additional Rules
    getAdditionalRules(params?: QueryParams): Observable<AdditionalRuleModelPagedResponseModel> {
        return this.get<AdditionalRuleModelPagedResponseModel>('/additional-rules', params)
            .pipe(map(response => response.data!));
    }

    getAdditionalRuleById(id: string): Observable<AdditionalRuleModel> {
        return this.get<AdditionalRuleModel>(`/additional-rules/${id}`)
            .pipe(map(response => response.data!));
    }

    createAdditionalRule(rule: AdditionalRuleModel): Observable<AdditionalRuleModel> {
        return this.post<AdditionalRuleModel>('/additional-rules', rule)
            .pipe(map(response => response.data!));
    }

    updateAdditionalRule(id: string, rule: AdditionalRuleModel): Observable<AdditionalRuleModel> {
        return this.put<AdditionalRuleModel>(`/additional-rules/${id}`, rule)
            .pipe(map(response => response.data!));
    }

    deleteAdditionalRule(id: string): Observable<void> {
        return this.delete<void>(`/additional-rules/${id}`)
            .pipe(map(() => undefined));
    }

    // Drainage Ventilation Rules
    getDrainageVentilationRules(params?: QueryParams): Observable<DrainageVentilationRuleModelPagedResponseModel> {
        return this.get<DrainageVentilationRuleModelPagedResponseModel>('/drainage-ventilation-rules', params)
            .pipe(map(response => response.data!));
    }

    getDrainageVentilationRuleById(id: string): Observable<DrainageVentilationRuleModel> {
        return this.get<DrainageVentilationRuleModel>(`/drainage-ventilation-rules/${id}`)
            .pipe(map(response => response.data!));
    }

    createDrainageVentilationRule(rule: DrainageVentilationRuleModel): Observable<DrainageVentilationRuleModel> {
        return this.post<DrainageVentilationRuleModel>('/drainage-ventilation-rules', rule)
            .pipe(map(response => response.data!));
    }

    updateDrainageVentilationRule(id: string, rule: DrainageVentilationRuleModel): Observable<DrainageVentilationRuleModel> {
        return this.put<DrainageVentilationRuleModel>(`/drainage-ventilation-rules/${id}`, rule)
            .pipe(map(response => response.data!));
    }

    deleteDrainageVentilationRule(id: string): Observable<void> {
        return this.delete<void>(`/drainage-ventilation-rules/${id}`)
            .pipe(map(() => undefined));
    }

    // Glazing Rules
    getGlazingRules(params?: QueryParams): Observable<GlazingRuleModelPagedResponseModel> {
        return this.get<GlazingRuleModelPagedResponseModel>('/glazing-rules', params)
            .pipe(map(response => response.data!));
    }

    getGlazingRuleById(id: string): Observable<GlazingRuleModel> {
        return this.get<GlazingRuleModel>(`/glazing-rules/${id}`)
            .pipe(map(response => response.data!));
    }

    createGlazingRule(rule: GlazingRuleModel): Observable<GlazingRuleModel> {
        return this.post<GlazingRuleModel>('/glazing-rules', rule)
            .pipe(map(response => response.data!));
    }

    updateGlazingRule(id: string, rule: GlazingRuleModel): Observable<GlazingRuleModel> {
        return this.put<GlazingRuleModel>(`/glazing-rules/${id}`, rule)
            .pipe(map(response => response.data!));
    }

    deleteGlazingRule(id: string): Observable<void> {
        return this.delete<void>(`/glazing-rules/${id}`)
            .pipe(map(() => undefined));
    }

    // Integrated Rules
    getIntegratedRules(params?: QueryParams): Observable<IntegratedRuleModelPagedResponseModel> {
        return this.get<IntegratedRuleModelPagedResponseModel>('/integrated-rules', params)
            .pipe(map(response => response.data!));
    }

    getIntegratedRuleById(id: string): Observable<IntegratedRuleModel> {
        return this.get<IntegratedRuleModel>(`/integrated-rules/${id}`)
            .pipe(map(response => response.data!));
    }

    createIntegratedRule(rule: IntegratedRuleModel): Observable<IntegratedRuleModel> {
        return this.post<IntegratedRuleModel>('/integrated-rules', rule)
            .pipe(map(response => response.data!));
    }

    updateIntegratedRule(id: string, rule: IntegratedRuleModel): Observable<IntegratedRuleModel> {
        return this.put<IntegratedRuleModel>(`/integrated-rules/${id}`, rule)
            .pipe(map(response => response.data!));
    }

    deleteIntegratedRule(id: string): Observable<void> {
        return this.delete<void>(`/integrated-rules/${id}`)
            .pipe(map(() => undefined));
    }

    // Joint Rules
    getJointRules(params?: QueryParams): Observable<JointRuleModelPagedResponseModel> {
        return this.get<JointRuleModelPagedResponseModel>('/joint-rules', params)
            .pipe(map(response => response.data!));
    }

    getJointRuleById(id: string): Observable<JointRuleModel> {
        return this.get<JointRuleModel>(`/joint-rules/${id}`)
            .pipe(map(response => response.data!));
    }

    createJointRule(rule: JointRuleModel): Observable<JointRuleModel> {      
        return this.post<JointRuleModel>('/joint-rules', rule)
            .pipe(map(response => response.data!));
    }

    updateJointRule(id: string, rule: JointRuleModel): Observable<JointRuleModel> {
        return this.put<JointRuleModel>(`/joint-rules/${id}`, rule)
            .pipe(map(response => response.data!));
    }

    deleteJointRule(id: string): Observable<void> {
        return this.delete<void>(`/joint-rules/${id}`)
            .pipe(map(() => undefined));
    }

    // Limitation Rules
    getLimitationRules(params?: QueryParams): Observable<LimitationRuleModelPagedResponseModel> {
        return this.get<LimitationRuleModelPagedResponseModel>('/limitation-rules', params)
            .pipe(map(response => response.data!));
    }

    getLimitationRuleById(id: string): Observable<LimitationRuleModel> {
        return this.get<LimitationRuleModel>(`/limitation-rules/${id}`)
            .pipe(map(response => response.data!));
    }

    createLimitationRule(rule: LimitationRuleModel): Observable<LimitationRuleModel> {
        return this.post<LimitationRuleModel>('/limitation-rules', rule)
            .pipe(map(response => response.data!));
    }

    updateLimitationRule(id: string, rule: LimitationRuleModel): Observable<LimitationRuleModel> {
        return this.put<LimitationRuleModel>(`/limitation-rules/${id}`, rule)
            .pipe(map(response => response.data!));
    }

    deleteLimitationRule(id: string): Observable<void> {
        return this.delete<void>(`/limitation-rules/${id}`)
            .pipe(map(() => undefined));
    }

    // Mating Rules
    getMatingRules(params?: QueryParams): Observable<MatingRuleModelPagedResponseModel> {
        return this.get<MatingRuleModelPagedResponseModel>('/mating-rules', params)
            .pipe(map(response => {
                console.log('Mating Rules Response:', response);
                return response.data!
            }));
    }

    getMatingRuleById(id: string): Observable<MatingRuleModel> {
        return this.get<MatingRuleModel>(`/mating-rules/${id}`)
            .pipe(map(response => response.data!));
    }

    createMatingRule(rule: MatingRuleModel): Observable<MatingRuleModel> {
        return this.post<MatingRuleModel>('/mating-rules', rule)
            .pipe(map(response => response.data!));
    }

    updateMatingRule(id: string, rule: MatingRuleModel): Observable<MatingRuleModel> {
        return this.put<MatingRuleModel>(`/mating-rules/${id}`, rule)
            .pipe(map(response => response.data!));
    }

    deleteMatingRule(id: string): Observable<void> {
        return this.delete<void>(`/mating-rules/${id}`)
            .pipe(map(() => undefined));
    }

    // Additional Rules - File operations
    addFileToAdditionalRule(id: string, fileId: string): Observable<void> {
        return this.post<void>(`/additional-rules/${id}/files/${fileId}`, {})
            .pipe(map(() => undefined));
    }

    removeFileFromAdditionalRule(id: string, fileId: string): Observable<void> {
        return this.delete<void>(`/additional-rules/${id}/files/${fileId}`)
            .pipe(map(() => undefined));
    }

    getAdditionalRuleFiles(id: string): Observable<any> {
        return this.get<any>(`/additional-rules/${id}/files`)
            .pipe(map(response => response.data!));
    }

    // Drainage Ventilation Rules - File operations
    addFileToDrainageVentilationRule(id: string, fileId: string): Observable<void> {
        return this.post<void>(`/drainage-ventilation-rules/${id}/files/${fileId}`, {})
            .pipe(map(() => undefined));
    }

    removeFileFromDrainageVentilationRule(id: string, fileId: string): Observable<void> {
        return this.delete<void>(`/drainage-ventilation-rules/${id}/files/${fileId}`)
            .pipe(map(() => undefined));
    }

    getDrainageVentilationRuleFiles(id: string): Observable<any> {
        return this.get<any>(`/drainage-ventilation-rules/${id}/files`)
            .pipe(map(response => response.data!));
    }

    // Glazing Rules - File operations
    addFileToGlazingRule(id: string, fileId: string): Observable<void> {
        return this.post<void>(`/glazing-rules/${id}/files/${fileId}`, {})
            .pipe(map(() => undefined));
    }

    removeFileFromGlazingRule(id: string, fileId: string): Observable<void> {
        return this.delete<void>(`/glazing-rules/${id}/files/${fileId}`)
            .pipe(map(() => undefined));
    }

    getGlazingRuleFiles(id: string): Observable<any> {
        return this.get<any>(`/glazing-rules/${id}/files`)
            .pipe(map(response => response.data!));
    }

    // Integrated Rules - File operations
    addFileToIntegratedRule(id: string, fileId: string): Observable<void> {
        return this.post<void>(`/integrated-rules/${id}/files/${fileId}`, {})
            .pipe(map(() => undefined));
    }

    removeFileFromIntegratedRule(id: string, fileId: string): Observable<void> {
        return this.delete<void>(`/integrated-rules/${id}/files/${fileId}`)
            .pipe(map(() => undefined));
    }

    getIntegratedRuleFiles(id: string): Observable<any> {
        return this.get<any>(`/integrated-rules/${id}/files`)
            .pipe(map(response => response.data!));
    }

    // Joint Rules - File operations
    addFileToJointRule(id: string, fileId: string): Observable<void> {
        return this.post<void>(`/joint-rules/${id}/files/${fileId}`, {})
            .pipe(map(() => undefined));
    }

    removeFileFromJointRule(id: string, fileId: string): Observable<void> {
        return this.delete<void>(`/joint-rules/${id}/files/${fileId}`)
            .pipe(map(() => undefined));
    }

    getJointRuleFiles(id: string): Observable<any> {
        return this.get<any>(`/joint-rules/${id}/files`)
            .pipe(map(response => response.data!));
    }

    // Limitation Rules - File operations
    addFileToLimitationRule(id: string, fileId: string): Observable<void> {
        return this.post<void>(`/limitation-rules/${id}/files/${fileId}`, {})
            .pipe(map(() => undefined));
    }

    removeFileFromLimitationRule(id: string, fileId: string): Observable<void> {
        return this.delete<void>(`/limitation-rules/${id}/files/${fileId}`)
            .pipe(map(() => undefined));
    }

    getLimitationRuleFiles(id: string): Observable<any> {
        return this.get<any>(`/limitation-rules/${id}/files`)
            .pipe(map(response => response.data!));
    }

    // Mating Rules - File operations
    addFileToMatingRule(id: string, fileId: string): Observable<void> {
        return this.post<void>(`/mating-rules/${id}/files/${fileId}`, {})
            .pipe(map(() => undefined));
    }

    removeFileFromMatingRule(id: string, fileId: string): Observable<void> {
        return this.delete<void>(`/mating-rules/${id}/files/${fileId}`)
            .pipe(map(() => undefined));
    }

    getMatingRuleFiles(id: string): Observable<any> {
        return this.get<any>(`/mating-rules/${id}/files`)
            .pipe(map(response => response.data!));
    }
}
